import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import Cart from '../cart/Cart';

const MAIN_SITE = process.env.REACT_APP_MAIN_SITE_URL || 'https://keyelectrolysis.com';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const { isAuthenticated, logout } = useAuth();
  const { cartCount } = useCart();
  const location = useLocation();

  // Check if we're on the main booking page
  const isBookingPage = location.pathname === '/';

  return (
    <>
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white shadow-sm z-50 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <a href={MAIN_SITE}>
                <img
                  src={`${MAIN_SITE}/Logo4.png`}
                  alt="Key Electrolysis Logo"
                  className="h-12 w-auto object-contain"
                />
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="sm:hidden text-primary p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Desktop Navigation - Hide when logged in */}
            {!isAuthenticated && (
              <div className="hidden sm:flex items-center justify-center space-x-8 flex-1">
                <a href={MAIN_SITE} className="text-primary hover:text-secondary transition-colors">
                  Home
                </a>
                <a href={`${MAIN_SITE}/services.html`} className="text-primary hover:text-secondary transition-colors">
                  What is Electrolysis?
                </a>
                <Link
                  to="/"
                  className={`transition-colors ${isBookingPage ? 'text-secondary font-medium' : 'text-primary hover:text-secondary'}`}
                >
                  Book a Session
                </Link>
                <a href={`${MAIN_SITE}/about.html`} className="text-primary hover:text-secondary transition-colors">
                  About Us
                </a>
                <a href={`${MAIN_SITE}/contact.html`} className="text-primary hover:text-secondary transition-colors">
                  Contact Us
                </a>
              </div>
            )}

            {/* User actions */}
            <div className="hidden sm:flex items-center space-x-4">
              <button
                onClick={() => setCartOpen(true)}
                className="text-primary hover:text-secondary transition-colors relative"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>

              {isAuthenticated ? (
                <div className="relative group">
                  <button className="text-primary hover:text-secondary transition-colors p-2">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </button>
                  <div className="absolute right-0 top-full pt-2 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="bg-white rounded-md shadow-lg py-1 border border-gray-100">
                      <Link to="/dashboard" className="block px-4 py-2 text-sm text-primary hover:bg-tertiary transition-colors">
                        Dashboard
                      </Link>
                      <Link to="/account" className="block px-4 py-2 text-sm text-primary hover:bg-tertiary transition-colors">
                        Account
                      </Link>
                      <button onClick={logout} className="block w-full text-left px-4 py-2 text-sm text-primary hover:bg-tertiary transition-colors">
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <Link to="/login" className="text-primary hover:text-secondary transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </Link>
              )}
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="sm:hidden pb-4">
              {!isAuthenticated && (
                <>
                  <a href={MAIN_SITE} className="block py-2 text-primary">Home</a>
                  <a href={`${MAIN_SITE}/services.html`} className="block py-2 text-primary">What is Electrolysis?</a>
                  <Link
                    to="/"
                    className={`block py-2 ${isBookingPage ? 'text-secondary font-medium' : 'text-primary'}`}
                  >
                    Book a Session
                  </Link>
                  <a href={`${MAIN_SITE}/about.html`} className="block py-2 text-primary">About Us</a>
                  <a href={`${MAIN_SITE}/contact.html`} className="block py-2 text-primary">Contact Us</a>
                </>
              )}
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" className="block py-2 text-primary">Dashboard</Link>
                  <Link to="/account" className="block py-2 text-primary">Account</Link>
                  <button onClick={logout} className="block py-2 text-primary">Logout</button>
                </>
              ) : (
                <Link to="/login" className="block py-2 text-primary">Login</Link>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Cart Sidebar */}
      {cartOpen && <Cart onClose={() => setCartOpen(false)} />}
    </>
  );
};

export default Header;
